import React, { useEffect, useState } from 'react';

import Modal from '../common/Modal';

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';
import { validateRequiredFields } from "../../utils/formValidation";
import { deepTrim } from "../../utils/formValidation";

const ClientDetailsPage = () => {

  const waApi = useWaApi();
  // Only a SYSTEM_ADMIN may change anything here; everyone else gets a read-only view. The service refuses
  // the writes too, so this just keeps the controls out of sight.
  const { canManageClientSetup } = useUserProfileAccess();
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    clientId: '',
    clientName: '',
    clientDescription: '',
    status: false,
  });

  const [editClientName, setEditClientName] = useState('');
  const [editClientDescription, setEditClientDescription] = useState('');
  const [editingClientId, setEditingClientId] = useState(null);
  const [actionErrors, setActionErrors] = useState({});
  const [createError, setCreateError] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);

  const initialFilters = {
    id: '',
    clientId: '',
    clientName: '',
    status: null
  };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const resetFilters = () => {
    clearMessages();
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(0);
  };

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(3);
  const [formError, setFormError] = useState({});

  const clearMessages = () => {
    setCreateError(null);
    setCreateMessage(null);
    setActionErrors({});
    setFormError({});
    setEditingClientId(null);
  };

  useEffect(() => {

    const fetchClients = async () => {
      try {
        // WAClientDetailController only exposes a cached full list, not a paged search,
        // so filtering and pagination for this page are done client-side.
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_CLIENT_DETAILS_ENDPOINT
        );

        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) {
          setAllClients(object || []);
        } else {
          setError(`Error: ${message}`);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching client details:', err);
        setError(err.message || 'An error occurred.');
        setLoading(false);
      }
    };

    fetchClients();
  }, [waApi]);

  useEffect(() => {
    if (modalOpen) {
      setFormError({});
    }
  }, [modalOpen]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;

    setNewClient((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    const statusValue = value === 'Active' ? true : value === 'Inactive' ? false : null;

    setFilters((prev) => ({ ...prev, status: statusValue }));
  };

  const applyFilters = () => {
    clearMessages();
    setAppliedFilters(filters);
    setPage(0);
  };

  const filteredClients = allClients.filter((client) => {
    if (appliedFilters.id && String(client.id) !== String(appliedFilters.id)) return false;
    if (appliedFilters.clientId && !client.clientId?.toLowerCase().includes(appliedFilters.clientId.toLowerCase())) return false;
    if (appliedFilters.clientName && !client.clientName?.toLowerCase().includes(appliedFilters.clientName.toLowerCase())) return false;
    if (appliedFilters.status !== null && client.status !== appliedFilters.status) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / size));
  const paginatedClients = filteredClients.slice(page * size, page * size + size);

  const handleEditClick = (client) => {
    clearMessages();
    setEditingClientId(client.id);
    setEditClientName(client.clientName || "");
    setEditClientDescription(client.clientDescription || "");
  };

  const refreshCache = async () => {
    clearMessages();
    try {
      const response = await waApi.get(
        process.env.REACT_APP_REFRESH_WA_CLIENT_DETAIL_CACHE_ENDPOINT
      );
      if (response.data.httpStatus === 200) {
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while refreshing the cache.');
    }
  };

  const toggleActivationStatus = async (id, currentStatus) => {
    clearMessages();
    try {
      const newStatus = !currentStatus;
      const response = await waApi.patch(
        `${process.env.REACT_APP_SET_WA_CLIENT_DETAIL_STATUS_ENDPOINT}/${id}`,
        { status: newStatus }
      );
      setAllClients((prev) =>
        prev.map((client) => client.id === id ? { ...client, status: newStatus } : client)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while updating the status.' }));
    }
  };

  const handleEditSubmit = async (id) => {
    clearMessages();
    try {
      const response = await waApi.put(
        `${process.env.REACT_APP_PUT_WA_CLIENT_DETAIL_ENDPOINT}/${id}`,
        {
          clientName: editClientName,
          clientDescription: editClientDescription
        }
      );

      setAllClients((prev) =>
        prev.map((client) => client.id === id
          ? { ...client, clientName: editClientName, clientDescription: editClientDescription }
          : client)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while updating the Client Detail.' }));
    } finally {
      setEditingClientId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client detail?')) {
      return;
    }
    setActionErrors((prev) => ({ ...prev, [id]: null }));
    try {
      await waApi.delete(`${process.env.REACT_APP_DELETE_WA_CLIENT_DETAIL_ENDPOINT}/${id}`);
      setAllClients((prev) => prev.filter((client) => client.id !== id));
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while deleting the Client Detail.' }));
    }
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    clearMessages();
    const validation = validateRequiredFields({
      clientId: { value: newClient.clientId, label: "Client Id" },
      clientName: { value: newClient.clientName, label: "Client" },
    });
    if (Object.keys(validation.errors).length > 0) {
      setFormError(validation.errors);
      return;
    }
    try {
      const payload = deepTrim(newClient);
      const response = await waApi.post(
        `${process.env.REACT_APP_SET_WA_CLIENT_DETAIL_ENDPOINT}`,
        payload
      );
      if (response.data.httpStatus === 200) {
        setAllClients((prev) => [...prev, response.data.object]);
        setNewClient({ clientId: '', clientName: '', clientDescription: '', status: true });
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while creating the client detail.');
    } finally {
      setModalOpen(false);
    }
  };

  const handleNextPage = () => {
    clearMessages();
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    clearMessages();
    if (page > 0) {
      setPage((prevPage) => prevPage - 1);
    }
  };

  const handleSizeChange = (e) => {
    setSize(Number(e.target.value));
    setPage(0);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading client details: {error}</div>;

  return (
    <div className="page-container">
      <h1>App Client Details</h1>
      <div className="flex-row filter-section">
        <div className='filter-item'>
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} className="w-auto">
            <option value={3}>default</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>
      </div>
      <h3>Filters</h3>
      <div className="flex-row filter-section">
        <div className="filter-item">
          <label>Id:</label>
          <input
            type="text"
            name="id"
            value={filters.id ?? ''}
            onChange={handleFilterInputChange}
            placeholder="Only numeric value"
          />
        </div>

        <div className="filter-item">
          <label>Client Id:</label>
          <input
            type="text"
            name="clientId"
            value={filters.clientId ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Client:</label>
          <input
            type="text"
            name="clientName"
            value={filters.clientName ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Status:</label>
          <select name="status" onChange={handleStatusChange} value={filters.status === null ? '' : filters.status ? 'Active' : 'Inactive'}>
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <button type="button" onClick={applyFilters}>Apply Filters</button>
        <button type="button" onClick={resetFilters}> Reset Filters</button>

      </div>

      {canManageClientSetup && (
        <div className="action-bar">
          <button
            onClick={() => {
              clearMessages();
              setNewClient({ clientId: '', clientName: '', clientDescription: '', status: false });
              setModalOpen(true);
            }}
          >
            + Create New
          </button>

          <button type="button" onClick={refreshCache}>
            Refresh Cache
          </button>
        </div>
      )}

      {createMessage && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createMessage}
        </div>
      )}

      {createError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createError}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => { clearMessages(); setModalOpen(false); }}>
        <div className="page-container">
          {formError && Object.values(formError).some(val => val !== null && val !== undefined && val !== "") && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
              <strong>Please fix the following errors:</strong>
              <ul style={{ margin: "8px 0 0 20px" }}>
                {Object.entries(formError)
                  .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                  .map(([key, err], index) => (
                    <li key={index}>
                      {typeof err === "object" ? JSON.stringify(err) : String(err)}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <form onSubmit={handleSubmit} className="standard-form">
            <h3>Create New Client Detail</h3>
            <div className="form-group">
              <label>Client Id:</label>
              <input
                type="text"
                name="clientId"
                value={newClient.clientId}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Client:</label>
              <input
                type="text"
                name="clientName"
                value={newClient.clientName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Client Description:</label>
              <input
                type="text"
                name="clientDescription"
                value={newClient.clientDescription}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Activation Status:</label>
              <input
                type="checkbox"
                name="status"
                checked={newClient.status}
                onChange={handleInputChange}
              />
            </div>
            <button type="submit">Create Client</button>
          </form>
        </div>
      </Modal>

      <h3>Existing Client Details</h3>
      {paginatedClients.length === 0 ? (
        <p>No client details available</p>
      ) : (
        <div className="table-responsive">
          <table className="notifications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Client Id</th>
                <th>Client</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Last Updated At</th>
                <th>Last Updated By</th>
                {canManageClientSetup && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedClients.map((client) => (
                <tr key={client.id}>
                  <td>{client.id}</td>
                  <td>{client.clientId}</td>
                  <td>
                    {editingClientId === client.id ? (
                      <input
                        type="text"
                        value={editClientName}
                        onChange={(e) => setEditClientName(e.target.value)}
                      />
                    ) : (
                      client.clientName
                    )}
                  </td>
                  <td>
                    {editingClientId === client.id ? (
                      <input
                        type="text"
                        value={editClientDescription}
                        onChange={(e) => setEditClientDescription(e.target.value)}
                      />
                    ) : (
                      client.clientDescription
                    )}
                  </td>
                  <td>{client.status ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(client.createdTimestamp).toLocaleString()}</td>
                  <td>{new Date(client.updatedTimestamp).toLocaleString()}</td>
                  <td>{client.lastUpdatedBy}</td>
                  {canManageClientSetup && (
                    <td>
                      <div className="action-buttons">
                        {editingClientId === client.id ? (
                          <>
                            <button onClick={() => handleEditSubmit(client.id)}>Save</button>
                            <button type="button" onClick={clearMessages}>Cancel</button>
                          </>
                        ) : (
                          <button onClick={() => handleEditClick(client)}>Edit</button>
                        )}
                        <button onClick={() => toggleActivationStatus(client.id, client.status)}>
                          {client.status ? 'Disable' : 'Enable'}
                        </button>
                        {/* <button onClick={() => handleDelete(client.id)}>Delete</button> */}
                      </div>
                      {actionErrors[client.id] && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                          {actionErrors[client.id]}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default ClientDetailsPage;
